import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Upload, Trash2, Loader2, Plus, Video, Image as ImageIcon, Link, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface BlogMediaItem {
  url: string;
  alt?: string;
  attribution?: string;
  type: 'image' | 'video';
  videoId?: string; // YouTube video ID
}

interface BlogImageGalleryManagerProps {
  images: BlogMediaItem[];
  onChange: (images: BlogMediaItem[]) => void;
  storagePath: string; // e.g., "blog/post-id"
  label?: string;
}

// Extract YouTube video ID from various URL formats or iframe code
function extractYouTubeId(input: string): string | null {
  // Check if it's an iframe - extract src
  const iframeMatch = input.match(/src="https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i);
  if (iframeMatch) return iframeMatch[1];

  // Check various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/i,
    /^([a-zA-Z0-9_-]{11})$/ // Just the ID
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[1];
  }

  return null;
}

export function BlogImageGalleryManager({ 
  images, 
  onChange, 
  storagePath,
  label = "Content Images & Videos"
}: BlogImageGalleryManagerProps) {
  const { toast } = useToast();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addType, setAddType] = useState<'image' | 'video' | 'url'>('image');
  const [urlInput, setUrlInput] = useState('');
  const [altInput, setAltInput] = useState('');
  const [attributionInput, setAttributionInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${storagePath}/gallery_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('town-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('town-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
      return null;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingIndex(-1); // -1 indicates adding new

    const newImages: BlogMediaItem[] = [];
    
    for (const file of Array.from(files)) {
      const url = await uploadImage(file);
      if (url) {
        newImages.push({ url, alt: '', attribution: '', type: 'image' });
      }
    }

    if (newImages.length > 0) {
      onChange([...images, ...newImages]);
      toast({
        title: `${newImages.length} image(s) added`,
        description: 'Remember to save your changes.',
      });
    }

    setUploadingIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddFromUrl = () => {
    if (!urlInput.trim()) {
      toast({
        title: 'URL required',
        description: 'Please enter an image URL.',
        variant: 'destructive',
      });
      return;
    }

    const newItem: BlogMediaItem = {
      url: urlInput.trim(),
      alt: altInput.trim() || '',
      attribution: attributionInput.trim() || '',
      type: 'image',
    };

    onChange([...images, newItem]);
    resetDialog();
    
    toast({
      title: 'Image added',
      description: 'Remember to save your changes.',
    });
  };

  const handleAddVideo = () => {
    const videoId = extractYouTubeId(urlInput.trim());
    
    if (!videoId) {
      toast({
        title: 'Invalid YouTube URL',
        description: 'Please enter a valid YouTube URL or iframe embed code.',
        variant: 'destructive',
      });
      return;
    }

    const newVideo: BlogMediaItem = {
      url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      type: 'video',
      videoId,
      alt: altInput.trim() || '',
      attribution: attributionInput.trim() || '',
    };

    onChange([...images, newVideo]);
    resetDialog();
    
    toast({
      title: 'Video added',
      description: 'Remember to save your changes.',
    });
  };

  const resetDialog = () => {
    setShowAddDialog(false);
    setUrlInput('');
    setAltInput('');
    setAttributionInput('');
    setAddType('image');
  };

  const updateItem = (index: number, updates: Partial<BlogMediaItem>) => {
    const updated = [...images];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeItem = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  // Generate embed code for inserting into content
  const getEmbedCode = (item: BlogMediaItem): string => {
    if (item.type === 'video' && item.videoId) {
      return `<div class="video-embed"><iframe width="560" height="315" src="https://www.youtube.com/embed/${item.videoId}" frameborder="0" allowfullscreen></iframe></div>`;
    }
    return `<img src="${item.url}" alt="${item.alt || ''}" />`;
  };

  const copyEmbedCode = (item: BlogMediaItem) => {
    const code = getEmbedCode(item);
    navigator.clipboard.writeText(code);
    toast({
      title: 'Embed code copied',
      description: 'Paste it into your content HTML.',
    });
  };

  const imageCount = images.filter(i => i.type !== 'video').length;
  const videoCount = images.filter(i => i.type === 'video').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-xs text-muted-foreground">
          {imageCount} image(s){videoCount > 0 && `, ${videoCount} video(s)`}
        </span>
      </div>

      {/* Media grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((item, index) => (
          <Card key={index} className="overflow-hidden group">
            <div className="relative aspect-video">
              {item.type === 'video' ? (
                <>
                  <img
                    src={item.url}
                    alt={item.alt || `Video thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="bg-red-600 text-white rounded-full p-2">
                      <Video className="h-4 w-4" />
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={item.alt || `Image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              {/* Hover actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyEmbedCode(item)}
                >
                  Copy Embed
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardContent className="p-2 space-y-1">
              <Input
                value={item.alt || ''}
                onChange={(e) => updateItem(index, { alt: e.target.value })}
                placeholder="Alt text"
                className="text-xs h-7"
              />
              <Input
                value={item.attribution || ''}
                onChange={(e) => updateItem(index, { attribution: e.target.value })}
                placeholder="Attribution"
                className="text-xs h-7"
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingIndex !== null}
          className="flex-1"
          size="sm"
        >
          {uploadingIndex === -1 ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Images
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => { setAddType('url'); setShowAddDialog(true); }}
          size="sm"
        >
          <Link className="h-4 w-4 mr-2" />
          URL
        </Button>
        <Button
          variant="outline"
          onClick={() => { setAddType('video'); setShowAddDialog(true); }}
          size="sm"
        >
          <Video className="h-4 w-4 mr-2" />
          YouTube
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Click "Copy Embed" on any media to get HTML code for pasting into your content.
      </p>

      {/* Add URL/Video Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {addType === 'video' ? 'Add YouTube Video' : 'Add Image from URL'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="mediaUrl">
                {addType === 'video' ? 'YouTube URL or Embed Code' : 'Image URL'}
              </Label>
              <Input
                id="mediaUrl"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={addType === 'video' ? 'https://youtu.be/...' : 'https://example.com/image.jpg'}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="mediaAlt">Alt Text (optional)</Label>
              <Input
                id="mediaAlt"
                value={altInput}
                onChange={(e) => setAltInput(e.target.value)}
                placeholder="Description for accessibility"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="mediaAttribution">Attribution (optional)</Label>
              <Input
                id="mediaAttribution"
                value={attributionInput}
                onChange={(e) => setAttributionInput(e.target.value)}
                placeholder="Photo by..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancel
            </Button>
            <Button onClick={addType === 'video' ? handleAddVideo : handleAddFromUrl} disabled={!urlInput.trim()}>
              Add {addType === 'video' ? 'Video' : 'Image'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
